(() => {
  'use strict';
  const instances = new WeakMap();
  const initialize = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal });
    const observers = [];
    const groups = [...root.querySelectorAll('[data-telo8-tabs]')];
    const selectTab = (group, tab, focus = false) => {
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
    };
    groups.forEach((group) => {
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      if (!tabs.length) return;
      selectTab(group, tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
      listen(group, 'click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && group.contains(tab)) selectTab(group, tab);
      });
      listen(group, 'keydown', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;
        let next = tabs.indexOf(tab);
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (next + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (next - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        selectTab(group, tabs[next], true);
      });
    });
    const dialog = root.querySelector('[data-telo8-dialog]');
    const dialogImage = root.querySelector('[data-telo8-dialog-image]');
    const dialogTitle = root.querySelector('[data-telo8-dialog-title]');
    const zoomToggle = root.querySelector('[data-telo8-zoom-toggle]');
    let lastOpener;
    const resetZoom = () => {
      dialog?.classList.remove('is-expanded');
      if (zoomToggle) {
        zoomToggle.textContent = zoomToggle.dataset.labelExpand;
        zoomToggle.setAttribute('aria-pressed', 'false');
      }
      const frame = dialog?.querySelector('.t8-dialog-image-wrap');
      if (frame) { frame.scrollTop = 0; frame.scrollLeft = 0; }
    };
    listen(root, 'click', (event) => {
      const trigger = event.target.closest('[data-telo8-zoom]');
      if (!trigger || !dialog || !dialogImage) return;
      lastOpener = trigger;
      resetZoom();
      dialogImage.src = trigger.dataset.zoomSrc;
      dialogImage.alt = trigger.dataset.zoomAlt || '';
      if (dialogTitle) dialogTitle.textContent = trigger.dataset.zoomAlt || '';
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('modal-is-open');
    });
    listen(root.querySelector('[data-telo8-dialog-close]'), 'click', () => dialog.close());
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    listen(dialog, 'close', () => {
      resetZoom();
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-is-open');
      lastOpener?.focus({ preventScroll: true });
    });
    listen(zoomToggle, 'click', () => {
      const expanded = dialog.classList.toggle('is-expanded');
      zoomToggle.textContent = expanded ? zoomToggle.dataset.labelFit : zoomToggle.dataset.labelExpand;
      zoomToggle.setAttribute('aria-pressed', String(expanded));
    });
    const nav = root.querySelector('[data-telo8-nav]');
    const navLinks = [...root.querySelectorAll('[data-telo8-nav-link]')];
    const sections = navLinks.map((link) => ({ link, target: document.getElementById(link.hash.slice(1)) })).filter((item) => item.target);
    const purchase = root.querySelector('[data-telo8-purchase]');
    const sticky = root.querySelector('[data-telo8-sticky]');
    let frameRequested = false;
    const updateScroll = () => {
      frameRequested = false;
      const header = document.querySelector('.site-header');
      const rect = header?.getBoundingClientRect();
      const position = header ? getComputedStyle(header).position : '';
      const offset = rect && ['sticky', 'fixed'].includes(position) && rect.top <= 2 && rect.bottom > 0 ? Math.round(rect.bottom) : 0;
      root.style.setProperty('--t8-header-offset', `${offset}px`);
      let active = null;
      const activationLine = offset + (nav?.offsetHeight || 60) + 130;
      sections.forEach((item) => { if (item.target.getBoundingClientRect().top <= activationLine) active = item.link; });
      navLinks.forEach((link) => {
        if (link === active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (sticky && purchase) {
        const visible = purchase.getBoundingClientRect().bottom < offset;
        sticky.classList.toggle('is-visible', visible);
        sticky.inert = !visible;
      }
    };
    const queueScroll = () => {
      if (!frameRequested) { frameRequested = true; requestAnimationFrame(updateScroll); }
    };
    listen(window, 'scroll', queueScroll, { passive: true });
    listen(window, 'resize', queueScroll, { passive: true });
    updateScroll();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reveals = [...root.querySelectorAll('[data-telo8-reveal]')];
    if ('IntersectionObserver' in window && !reduced.matches) {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.04, rootMargin: '0px 0px 30px 0px' });
      reveals.forEach((element) => revealObserver.observe(element));
      observers.push(revealObserver);
      root.classList.add('t8-enhanced');
    }
    listen(root, 'change', (event) => {
      if (!event.target.matches('[data-variant-select]')) return;
      const option = event.target.selectedOptions[0];
      if (!option) return;
      root.querySelectorAll('[data-telo8-price]').forEach((element) => { element.textContent = option.dataset.price; });
    });
    const productForm = root.querySelector('[data-product-form]');
    const addButton = productForm?.querySelector('[data-add-button]');
    if (addButton) {
      const purchaseObserver = new MutationObserver(() => {
        const option = productForm.querySelector('[data-variant-select]')?.selectedOptions[0];
        const label = productForm.dataset.submitting === 'true' ? root.dataset.addingLabel : option?.dataset.available === 'true' ? addButton.dataset.defaultLabel : root.dataset.soldOutLabel;
        if (label && addButton.textContent !== label) addButton.textContent = label;
      });
      purchaseObserver.observe(addButton, { childList: true, subtree: true });
      observers.push(purchaseObserver);
    }
    listen(document, 'shopify:block:select', (event) => {
      if (!root.contains(event.target)) return;
      event.target.classList.add('is-revealed');
      event.target.querySelectorAll('[data-telo8-reveal]').forEach((element) => element.classList.add('is-revealed'));
    });
    instances.set(root, () => {
      if (dialog?.open) dialog.close();
      controller.abort();
      observers.forEach((observer) => observer.disconnect());
      instances.delete(root);
    });
  };
  const initializeWithin = (scope) => {
    if (scope.matches?.('[data-telo8-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-telo8-root]').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-telo8-root]').forEach((root) => instances.get(root)?.());
  });
})();
